import React, {
    useCallback,
    useMemo,
    useState,
} from 'react'
import isNumber from 'lodash/isNumber'

import usePhotoContext from '@src/context/PhotosContext/usePhotoContext'
import ConfirmDialog from '@src/shared-components/ConfirmDialog'
import useT from '@src/hooks/useT/useT'
import Carousel from '@src/shared-components/Carousel'
import FullScreen from '@src/shared-components/FullScreen'
import {
    MAX_NUMBER_OF_PHOTOS, MIN_NEEDED_NUMBERS_OF_PHOTOS,
} from '@src/constants/logic'
import useFlowRouter from '@src/shared-components/FlowRouter/useFlowRouter'
import APP_FLOW_STEPS from '@src/constants/FlowSteps'
import useSaveAttachmentsFromContext from '@src/hooks/useConfirnGatewayInstallation/useSaveAttachmentsFromContext'
import Loading from '@src/shared-components/Loading'

import ErrorPage from '../Error/ErrorPage'

import TakePhotoOverviewPage from './components/TakePhotoOverviewPage'

function TakePhotoOverview(): JSX.Element {
    const {
        t,
    } = useT()
    const {
        photos,
        removePhoto,
    } = usePhotoContext()
    const [
        fullScreen,
        setFullScreen,
    ] = useState<boolean>(false)
    const [
        showRemoveConfirmIndex,
        setShowRemoveConfirmIndex,
    ] = useState<number>()
    const [
        carouselIndex,
        setCarouselIndex,
    ] = useState(0)
    const {
        goToStep,
    } = useFlowRouter()
    const onSuccess = useCallback(() => {
        goToStep(APP_FLOW_STEPS.locationConfirm)
    }, [goToStep])
    const {
        isLoading,
        error,
        save,
        resetErrors,
    } = useSaveAttachmentsFromContext(onSuccess)

    const handleNextClick = useCallback(() => {
        goToStep(APP_FLOW_STEPS.takePhoto)
    }, [goToStep])

    const handleContinueClick = useCallback(() => {
        save()
    }, [save])

    const onRemoveHandle = useCallback((i: number) => {
        setShowRemoveConfirmIndex(i)
    }, [])

    const onAfterError = useCallback(() => {
        resetErrors()
        goToStep(APP_FLOW_STEPS.scanQR)
    }, [
        goToStep,
        resetErrors,
    ])

    const onConfirmRemove = useCallback((remove: boolean) => {
        if (remove && isNumber(showRemoveConfirmIndex)) {
            removePhoto(showRemoveConfirmIndex)
        }
        setShowRemoveConfirmIndex(undefined)
    }, [
        removePhoto,
        showRemoveConfirmIndex,
    ])

    const allowedTakePhoto = useMemo(() => {
        return photos.length < MAX_NUMBER_OF_PHOTOS
    }, [photos.length])

    const allowedContinue = useMemo(() => {
        return photos.length >= MIN_NEEDED_NUMBERS_OF_PHOTOS
    }, [photos])

    if (!photos.length) {
        goToStep(APP_FLOW_STEPS.takePhotoOverviewEmpty)
    }

    if (error) {
        return (
            <ErrorPage
                onBackClick={onAfterError}
                backButtonTitle={t('TRY_AGAIN_BUTTON_TITLE')}
            />
        )
    }

    return fullScreen ? (
        <FullScreen
            photos={photos}
            closeFullScreen={() => {
                setFullScreen(false)
            }}
            index={carouselIndex}
            setIndex={setCarouselIndex}
        />
    ) : (
        <Loading
            isLoading={isLoading}
            showSpinner
            label={t('UPLOAD_IN_PROGRESS')}
        >
            <TakePhotoOverviewPage
                onTakePhotoClick={handleNextClick}
                allowedContinue={allowedContinue}
                onContinueClick={handleContinueClick}
                allowedTakePhoto={allowedTakePhoto}
            >
                <Carousel
                    photos={photos}
                    removePhoto={onRemoveHandle}
                    setFullScreen={setFullScreen}
                    index={carouselIndex}
                    setIndex={setCarouselIndex}
                />
            </TakePhotoOverviewPage>
            <ConfirmDialog
                dialogTitle={t('CONFIRM_DELETE') as string}
                open={isNumber(showRemoveConfirmIndex)}
                dialogContent={t('CONFIRM_DELETE_MESSAGE') as string}
                handleClose={onConfirmRemove}
                negativeLabel={t('CANCEL') as string}
                positiveLabel={t('DELETE') as string}
            />
        </Loading>
    )
}

export default TakePhotoOverview
